/**
 * Pure mapping between the offline MeasurementSession document and its
 * sketch.* rows (ADR 0105, N1 measurement backup).
 *
 * THE TRANSPORT IS THE DOCUMENT. The whole serialized session rides in
 * `measurement_sessions.client_state` — whose 2026-08-12 column comment
 * already promised "lossless device-neutral session state" — and the pull
 * rebuilds from that document alone. The relational slot/feature rows are a
 * queryable PROJECTION for future integration (and DB-level sanity), never
 * the source of truth: the 2026-08-12 relational schema predates height
 * slots (the slot_kind CHECK refuses them), the multi-reading candidates
 * list (#124), and roughShape. A relational-only mapping was silently
 * dropping all three — the exact "reliably captured" failure the owner named
 * in the N1 ruling. Anything the projection cannot hold still travels,
 * because the document does. (This is the remeasure precedent —
 * original_snapshot/measurements as JSONB — not a new shape.)
 *
 * This deliberately does not reuse `sketchToRows`: room sync replaces
 * geometry, whereas a session travels as a reviewable batch.
 */
import { serializeMeasurementSession, deserializeMeasurementSession } from './measurement-session.js';

const numberOrNull = (value) => (Number.isFinite(value) ? value : null);

/**
 * Converts a durable measurement session into its project-owned rows.
 * `projectName` mirrors the remeasure mapping's derived "Client — Opportunity"
 * label; the project row is the RLS ownership anchor and must upsert first.
 */
export function measurementSessionToRows(session, { ownerId, projectId, clientId = null, projectName = 'Untitled project' } = {}) {
  if (!projectId) throw new Error('Measurement session sync requires a project id.');
  const doc = serializeMeasurementSession(session);
  const base = { client_id: clientId };
  return {
    project: {
      id: projectId, owner_id: ownerId, name: projectName,
      salesforce_opportunity_id: null, client_id: clientId,
    },
    session: {
      id: doc.id, project_id: projectId, room_id: doc.roomId ?? null,
      template_id: doc.templateId ?? null, status: doc.status,
      numbering: doc.numbering,
      client_state: doc,
      ...base,
    },
    // Projection: only what the 20260812 slot_kind CHECK admits. Height
    // slots (ceiling_height/corner_height) would be REFUSED by the database;
    // they live in the document above, losslessly.
    slots: doc.slots
      .filter((slot) => slot.kind === 'perimeter_length' || slot.kind === 'feature_dimension')
      .map((slot) => ({
        id: slot.id, session_id: doc.id, wall_label: slot.wallLabel, slot_kind: slot.kind,
        value_units: numberOrNull(slot.valueUnits), unit_of_measure: slot.unit ?? 'in',
        source_kind: slot.source?.kind ?? null, source_device: slot.source?.device ?? null,
        status: slot.status, captured_at: slot.capturedAt ?? null, ...base,
      })),
    features: doc.features.map((feature) => ({
      id: feature.id, session_id: doc.id, feature_type: feature.type,
      host_surface: feature.hostSurface === 'ceiling' ? 'ceiling' : 'wall',
      host_wall_label: feature.hostSurface === 'ceiling' ? null : feature.hostWallLabel,
      reference_walls: feature.hostSurface === 'ceiling' ? (feature.referenceWalls ?? []) : [],
      measurements: feature.measurements ?? [], status: feature.status, ...base,
    })),
  };
}

/**
 * Rebuilds the offline session document from ONE pulled session row — the
 * document in client_state, validated whole. Rejecting a malformed payload
 * outright (rather than best-effort restoring from the projection rows)
 * keeps one author for the session's shape: a partial restore would be a
 * second, subtly different deserializer.
 */
export function rowToMeasurementSession(row) {
  return deserializeMeasurementSession(row.client_state);
}
